/**
 * AIToolCategories displays the list of tool categoriesToRender that are displayed in
 * the AI Tools.
 */

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import React, {useCallback, useEffect, useRef, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import Pill from "@khanacademy/wonder-blocks-pill";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {mediaQueries, pureXsMax} from "@khan/shared-styles";

import {
    i18nToolCategory,
    toCEDARToolCategory,
    ToolCategories,
} from "./ai-tools-list.ts";
import useMediaQuery from "./hooks/use-media-query.ts";

import caretRightIcon from "@phosphor-icons/core/bold/caret-right-bold.svg";
import dotsNineBoldIcon from "@phosphor-icons/core/bold/dots-nine-bold.svg";
import circle from "@phosphor-icons/core/fill/circle-fill.svg";
import starIconFilled from "@phosphor-icons/core/fill/star-fill.svg";

type Props = {
    selected: ToolCategories;
    setSelected: (selected: ToolCategories) => void;
    categoriesToRender: ToolCategories[];
};

const AIToolCategories = ({
    selected,
    setSelected,
    categoriesToRender,
}: Props): React.ReactElement => {
    const isXsSmallScreen = useMediaQuery(`(max-width: ${pureXsMax})`);
    const [hasClickedCaret, setHasClickedCaret] = useState(false);

    const categoriesGroupRef = useRef<HTMLDivElement>(null);
    const categoriesRef = useRef<HTMLButtonElement[]>([]);
    const lastSelected = useRef<ToolCategories>(selected);

    const getCategoryIndex = (toolCategory: ToolCategories) =>
        categoriesToRender.indexOf(toolCategory);
    const getCategoryFromIndex = (index: number) => categoriesToRender[index];

    useEffect(() => {
        // The rerender was causing the focus to be lost, so we need to
        // refocus the selected category.
        if (lastSelected.current !== selected) {
            lastSelected.current = selected;
            categoriesRef.current[getCategoryIndex(selected)].focus();
        }
    });

    const handlePillSelect = (toolCategory: ToolCategories) => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiTeacherToolsCategorySelected",
            eventSchemaVersion: 3,
            toolCategory: toCEDARToolCategory(toolCategory),
        });

        setSelected(toolCategory);
    };

    const handleCaretClick = () => {
        if (isXsSmallScreen) {
            return () => {
                setHasClickedCaret(true);

                const categoriesToRender = categoriesGroupRef.current;
                if (categoriesToRender) {
                    categoriesToRender.scrollLeft += 200;
                }
            };
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const selectedIndex = getCategoryIndex(selected);
        switch (e.key) {
            case "ArrowRight":
            case "ArrowDown":
                const nextIndex =
                    (selectedIndex + 1) % categoriesToRender.length;
                handlePillSelect(getCategoryFromIndex(nextIndex));
                break;
            case "ArrowLeft":
            case "ArrowUp":
                const prevIndex =
                    (selectedIndex - 1 + categoriesToRender.length) %
                    categoriesToRender.length;
                handlePillSelect(getCategoryFromIndex(prevIndex));
                break;
            default:
                break;
        }
    };

    const chooseColor = useCallback((t: ToolCategories) => {
        // Chooses icon with color corresponding to the
        // tool category
        switch (t) {
            case ToolCategories.Plan:
                return color.fadedPurple24;
            case ToolCategories.Create:
                return color.fadedGreen24;
            case ToolCategories.Differentiate:
                return color.fadedRed24;
            case ToolCategories.Support:
                return color.fadedBlue24;
            case ToolCategories.Learn:
                return color.fadedGold24;
            default:
                return undefined;
        }
    }, []);

    return (
        <>
            <View
                style={styles.categoriesToRender}
                role="radiogroup"
                ref={categoriesGroupRef}
                aria-label={t`Filter tool categories`}
                onKeyDown={handleKeyDown}
            >
                {categoriesToRender.map((toolCategory, i) => {
                    const kind =
                        toolCategory === selected ? "accent" : "transparent";
                    const checked = toolCategory === selected ? true : false;
                    return (
                        <Pill
                            key={toolCategory}
                            size="large"
                            kind={kind}
                            onClick={() => handlePillSelect(toolCategory)}
                            style={styles.pill}
                            role="radio"
                            aria-checked={checked}
                            tabIndex={checked ? 0 : -1}
                            ref={(node) => {
                                if (node) {
                                    categoriesRef.current[i] =
                                        node as HTMLButtonElement;
                                }
                            }}
                        >
                            <View style={styles.pillContent}>
                                {toolCategory === ToolCategories.Favorites ? (
                                    <PhosphorIcon
                                        icon={starIconFilled}
                                        color={color.gold}
                                        size="small"
                                        style={styles.categoryIcon}
                                    />
                                ) : null}
                                {toolCategory === ToolCategories.All ? (
                                    <PhosphorIcon
                                        icon={dotsNineBoldIcon}
                                        size="small"
                                        style={styles.categoryIcon}
                                    />
                                ) : null}
                                {chooseColor(toolCategory) && (
                                    <PhosphorIcon
                                        icon={circle}
                                        color={chooseColor(toolCategory)}
                                        size="small"
                                        style={styles.categoryIcon}
                                    />
                                )}
                                {i18nToolCategory(toolCategory)}
                            </View>
                        </Pill>
                    );
                })}
            </View>
            {isXsSmallScreen && !hasClickedCaret && (
                <IconButton
                    icon={caretRightIcon}
                    aria-label={t`Caret right`}
                    onClick={handleCaretClick()}
                    kind="tertiary"
                    actionType="neutral"
                    size="xsmall"
                    style={styles.rightCaret}
                />
            )}
        </>
    );
};

export default AIToolCategories;

const styles = StyleSheet.create({
    categoriesToRender: {
        flexDirection: "row",
        [mediaQueries.xsOrSmaller]: {
            width: "100%",
            overflow: "scroll",
        },
    },

    categoryIcon: {
        marginRight: "8px",
    },
    pill: {
        height: 40,
        marginRight: spacing.xSmall_8,
        // For the outline
        marginTop: spacing.xxxxSmall_2,
        marginBottom: spacing.xxxxSmall_2,

        [mediaQueries.xsOrSmaller]: {
            height: 32,
            marginTop: spacing.xxxSmall_4,
            marginBottom: spacing.xxxSmall_4,
        },
    },

    pillContent: {
        flexDirection: "row",
        alignItems: "center",
    },

    rightCaret: {
        position: "absolute",
        top: spacing.medium_16,
        right: spacing.medium_16,
        width: 24,
        height: 24,
        border: "1px solid #21242C29",
        borderRadius: 100,
        backgroundColor: "white",
        boxShadow: "0px 4px 4px 0px #0000001A",
    },
});
